import { ChipSize, UIComponentSize } from "@/types/shared/uiComponents";
import { alpha, Theme, SxProps } from "@mui/material";

interface ChipStyleOptions {
  size?: ChipSize;
  clickable?: boolean;
}

const getSizeStyles = (size: ChipSize) => {
  if (size === UIComponentSize.Small) {
    return {
      height: 24,
      fontSize: 12,
      "& .MuiChip-label": { px: 1 },
      "& .MuiChip-icon": { fontSize: 14, ml: 0.5 },
    };
  }
  return {
    height: 28,
    fontSize: 13,
    "& .MuiChip-label": { px: 1.5 },
    "& .MuiChip-icon": { fontSize: 16, ml: 0.5 },
  };
};

/**
 * Base chip styles shared across all task chip variants
 */
export const getChipBase = (options: ChipStyleOptions = {}): SxProps<Theme> => {
  const { size = UIComponentSize.Medium } = options;
  return {
    ...getSizeStyles(size),
    fontWeight: 500,
    borderRadius: "6px",
  };
};

/**
 * Filled chip style - used when a value is selected
 * Primary color with light background
 */
export const getFilledChipStyles = (options: ChipStyleOptions = {}): SxProps<Theme> => {
  const { size = UIComponentSize.Medium, clickable = true } = options;
  return {
    ...getChipBase({ size }),
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
    color: "primary.main",
    cursor: clickable ? "pointer" : "default",
    "&:hover": clickable ? { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18) } : {},
  };
};

/**
 * Empty chip style - used when no value is selected (placeholder state)
 * Dashed border with transparent background
 */
export const getEmptyChipStyles = (options: ChipStyleOptions = {}): SxProps<Theme> => {
  const { size = UIComponentSize.Medium, clickable = true } = options;
  return {
    ...getChipBase({ size }),
    bgcolor: "transparent",
    color: "text.secondary",
    border: "1px dashed",
    borderColor: "divider",
    cursor: clickable ? "pointer" : "default",
    "&:hover": clickable ? { borderColor: "text.secondary", bgcolor: "action.hover" } : {},
  };
};

/**
 * Suggestion chip style - used for quick-add template suggestions
 * Solid border that highlights on hover
 */
export const getSuggestionChipStyles = (options: ChipStyleOptions = {}): SxProps<Theme> => {
  const { size = UIComponentSize.Medium } = options;
  const sizeStyles = size === "small" ? { height: 22, fontSize: 11 } : { height: 26, fontSize: 12 };

  return {
    ...sizeStyles,
    fontWeight: 500,
    borderRadius: "6px",
    bgcolor: "transparent",
    border: "1px solid",
    borderColor: "divider",
    color: "text.secondary",
    cursor: "pointer",
    transition: "all 0.15s",
    "&:hover": {
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
      borderColor: "primary.main",
      color: "primary.main",
    },
  };
};

/**
 * Due date chip style - colored based on urgency
 */
export const getDueDateChipStyles = (
  isOverdue: boolean,
  hasDueDate: boolean,
  options: ChipStyleOptions = {}
): SxProps<Theme> => {
  const { size = UIComponentSize.Medium, clickable = true } = options;

  if (!hasDueDate) {
    return getEmptyChipStyles({ size, clickable });
  }

  return {
    ...getChipBase({ size }),
    bgcolor: (theme) => alpha(theme.palette[isOverdue ? "error" : "primary"].main, 0.1),
    color: isOverdue ? "error.main" : "primary.main",
    cursor: clickable ? "pointer" : "default",
  };
};
